/**
 * Hora Advanced Agenda — reusable slide-in drawer shell.
 *
 * Vanilla TypeScript, no framework. The shell is a chrome component: it owns
 * positioning / motion / focus trap / keyboard + backdrop close, and nothing
 * else. Content comes in as an HTML string (server-rendered) and is injected
 * verbatim into the panel's inner wrapper.
 *
 * Intended to serve both the calendar view (phase 2) and the agenda list view
 * (future), so zero knowledge of section data lives here.
 */
import { initAddToCalendarTracking } from "../shared/analytics";
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const CLOSE_ANIM_MS = 240;
// Anchor the singleton on `window` so separate bundles (calendar view + list
// view) resolve to the same DOM instance when both shortcodes live on the
// same page. Without this, each bundle's module-local `singleton` would build
// its own drawer and only one would respond per click.
export function getDrawer() {
    if (!window.__horaAgendaDrawer) {
        window.__horaAgendaDrawer = build();
    }
    return window.__horaAgendaDrawer;
}
function build() {
    const root = document.createElement("div");
    root.className = "hora-drawer";
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-modal", "true");
    root.setAttribute("hidden", "");
    const backdrop = document.createElement("div");
    backdrop.className = "hora-drawer__backdrop";
    backdrop.setAttribute("data-drawer-close", "");
    const panel = document.createElement("div");
    panel.className = "hora-drawer__panel";
    panel.setAttribute("tabindex", "-1");
    const inner = document.createElement("div");
    inner.className = "hora-drawer__inner";
    panel.appendChild(inner);
    root.appendChild(backdrop);
    root.appendChild(panel);
    document.body.appendChild(root);
    // GA4 add-to-calendar tracking for the drawer's add-to-calendar link.
    // The drawer is appended to <body>, so clicks inside it don't bubble to
    // the calendar / list containers — each surface needs its own listener.
    initAddToCalendarTracking(root, "drawer");
    const navHandlers = new Set();
    const closeHandlers = new Set();
    let opened = false;
    let lastFocus = null;
    let savedBodyOverflow = "";
    function open(html, opts) {
        var _a;
        if (!opened) {
            lastFocus =
                ((_a = opts === null || opts === void 0 ? void 0 : opts.restoreFocusTo) !== null && _a !== void 0 ? _a : document.activeElement) || null;
            savedBodyOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            root.removeAttribute("hidden");
            // Next frame, flip the --open class so CSS transitions run from the
            // pre-paint state to the open state.
            requestAnimationFrame(() => root.classList.add("hora-drawer--open"));
            window.addEventListener("keydown", onGlobalKey, true);
            opened = true;
        }
        setContent(html);
    }
    function setContent(html) {
        inner.innerHTML = html;
        // Focus the close button (or first focusable) after content updates so
        // screen readers announce the dialog, and keyboard users land inside.
        queueMicrotask(focusFirst);
    }
    function close() {
        if (!opened)
            return;
        opened = false;
        root.classList.remove("hora-drawer--open");
        window.removeEventListener("keydown", onGlobalKey, true);
        document.body.style.overflow = savedBodyOverflow;
        const reducedMotion = typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const delay = reducedMotion ? 0 : CLOSE_ANIM_MS;
        window.setTimeout(() => {
            // Guard against a reopen that raced with the close timer.
            if (!opened) {
                root.setAttribute("hidden", "");
                inner.innerHTML = "";
            }
        }, delay);
        if (lastFocus && document.contains(lastFocus)) {
            // preventScroll: keep the calendar's scroll state from prev/next nav
            // intact — default focus() would scroll the original card back into view.
            lastFocus.focus({ preventScroll: true });
        }
        for (const h of closeHandlers)
            h();
    }
    function destroy() {
        close();
        root.remove();
        navHandlers.clear();
        closeHandlers.clear();
        window.__horaAgendaDrawer = undefined;
    }
    function onNavigate(handler) {
        navHandlers.add(handler);
        return () => {
            navHandlers.delete(handler);
        };
    }
    function onClose(handler) {
        closeHandlers.add(handler);
        return () => {
            closeHandlers.delete(handler);
        };
    }
    root.addEventListener("click", (e) => {
        const target = e.target;
        if (!target)
            return;
        if (target.closest("[data-drawer-close]")) {
            e.preventDefault();
            close();
            return;
        }
        const navBtn = target.closest("[data-nav]");
        if (navBtn && panel.contains(navBtn)) {
            if (navBtn.hasAttribute("disabled"))
                return;
            const direction = navBtn.getAttribute("data-nav");
            if (direction !== "prev" && direction !== "next")
                return;
            const rawId = navBtn.getAttribute("data-target-id");
            const targetId = rawId && rawId !== "" ? rawId : null;
            for (const h of navHandlers) {
                h({ direction, targetId });
            }
            return;
        }
        // Subsection click inside the drawer body: navigate the drawer to that
        // child section. Skip any element with data-section-id that lives on the
        // drawer content wrapper itself (the wrapper sets data-section-id for the
        // currently displayed section).
        const sectionEl = target.closest(".hora-subsection[data-section-id]");
        if (sectionEl && panel.contains(sectionEl)) {
            const targetId = sectionEl.getAttribute("data-section-id");
            if (targetId && targetId !== "") {
                e.preventDefault();
                for (const h of navHandlers) {
                    h({ direction: "select", targetId });
                }
            }
        }
    });
    function focusFirst() {
        if (!opened)
            return;
        const closeBtn = inner.querySelector(".hora-drawer__close");
        if (closeBtn) {
            closeBtn.focus({ preventScroll: true });
            return;
        }
        const first = inner.querySelector(FOCUSABLE_SELECTOR);
        if (first) {
            first.focus({ preventScroll: true });
        }
        else {
            panel.focus({ preventScroll: true });
        }
    }
    function onGlobalKey(e) {
        if (!opened)
            return;
        if (e.key === "Escape") {
            e.preventDefault();
            close();
            return;
        }
        if (e.key === "Tab") {
            trapFocus(e);
        }
    }
    function trapFocus(e) {
        const nodes = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => el.offsetParent !== null || el === panel);
        if (nodes.length === 0) {
            e.preventDefault();
            panel.focus();
            return;
        }
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        const active = document.activeElement;
        if (e.shiftKey) {
            if (active === first || !panel.contains(active)) {
                e.preventDefault();
                last.focus();
            }
        }
        else {
            if (active === last) {
                e.preventDefault();
                first.focus();
            }
        }
    }
    return {
        open,
        setContent,
        close,
        isOpen: () => opened,
        onNavigate,
        onClose,
        destroy,
    };
}
